import { useRef, useEffect } from 'react';
import { useThemeContext } from '@/contexts/ThemeContext';

export function GradientBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { theme } = useThemeContext();
  const mouse = useRef({ x: 0.5, y: 0.5, tx: 0.5, ty: 0.5 });
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    function resize() {
      if (!canvas) return;
      canvas.width = canvas.offsetWidth * devicePixelRatio;
      canvas.height = canvas.offsetHeight * devicePixelRatio;
    }

    function draw() {
      if (!canvas || !ctx) return;
      const w = canvas.width;
      const h = canvas.height;
      const mx = mouse.current;
      const cx = mx.x * w;
      const cy = mx.y * h;
      // Always read from DOM so it stays in sync with any external class change
      const isDark = document.body.classList.contains('dark');
      ctx.clearRect(0, 0, w, h);

      if (isDark) {
        const base = ctx.createLinearGradient(0, 0, w, h);
        base.addColorStop(0, '#080412');
        base.addColorStop(0.5, '#0a0a1a');
        base.addColorStop(1, '#06060e');
        ctx.fillStyle = base;
        ctx.fillRect(0, 0, w, h);

        const spots = [
          { x: cx,            y: cy,            r: w * 0.55, c1: 'rgba(0,210,220,0.18)',   c2: 'rgba(0,210,220,0)' },
          { x: cx * 0.3,      y: cy * 0.35,     r: w * 0.55, c1: 'rgba(180,100,255,0.22)', c2: 'rgba(180,100,255,0)' },
          { x: w - cx * 0.4,  y: h - cy * 0.4,  r: w * 0.50, c1: 'rgba(56,200,160,0.14)',  c2: 'rgba(56,200,160,0)' },
          { x: w - cx * 0.25, y: cy * 0.6,      r: w * 0.50, c1: 'rgba(100,80,240,0.22)',  c2: 'rgba(100,80,240,0)' },
          { x: cx * 0.7,      y: h - cy * 0.3,  r: w * 0.45, c1: 'rgba(220,100,230,0.14)', c2: 'rgba(220,100,230,0)' },
        ];
        for (const s of spots) {
          const g = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.r);
          g.addColorStop(0, s.c1);
          g.addColorStop(1, s.c2);
          ctx.fillStyle = g;
          ctx.fillRect(0, 0, w, h);
        }
        return;
      }

      const base = ctx.createLinearGradient(0, 0, w, h);
      base.addColorStop(0, '#fef4e8');
      base.addColorStop(0.3, '#eeeaf5');
      base.addColorStop(0.6, '#e6f5ed');
      base.addColorStop(1, '#eaf2fb');
      ctx.fillStyle = base;
      ctx.fillRect(0, 0, w, h);

      const spots = [
        { x: cx,           y: cy,           r: w * 0.55, c1: 'rgba(240,133,33,0.14)',  c2: 'rgba(240,133,33,0)' },
        { x: cx * 0.3,     y: cy * 0.3,     r: w * 0.5,  c1: 'rgba(52,47,130,0.10)',   c2: 'rgba(52,47,130,0)' },
        { x: w - cx * 0.5, y: h - cy * 0.5, r: w * 0.55, c1: 'rgba(32,177,90,0.12)',   c2: 'rgba(32,177,90,0)' },
        { x: w - cx * 0.3, y: cy * 0.6,     r: w * 0.45, c1: 'rgba(64,184,233,0.12)',  c2: 'rgba(64,184,233,0)' },
        { x: cx * 0.8,     y: h - cy * 0.4, r: w * 0.4,  c1: 'rgba(239,83,149,0.08)',  c2: 'rgba(239,83,149,0)' },
      ];
      for (const s of spots) {
        const g = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.r);
        g.addColorStop(0, s.c1);
        g.addColorStop(1, s.c2);
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, h);
      }
    }

    function animate() {
      const mx = mouse.current;
      mx.x += (mx.tx - mx.x) * 0.08;
      mx.y += (mx.ty - mx.y) * 0.08;
      draw();
      rafRef.current = requestAnimationFrame(animate);
    }

    function onMove(e: MouseEvent) {
      if (!canvas) return;
      const r = canvas.getBoundingClientRect();
      mouse.current.tx = (e.clientX - r.left) / r.width;
      mouse.current.ty = (e.clientY - r.top) / r.height;
    }

    function onLeave() {
      mouse.current.tx = 0.5;
      mouse.current.ty = 0.5;
    }

    resize();
    window.addEventListener('resize', resize);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseleave', onLeave);
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
    };
  // Re-initialize when theme changes so canvas picks up the new palette immediately
  }, [theme]);

  return (
    <canvas
      ref={canvasRef}
      id="gradient-bg"
    />
  );
}
